import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import MasterSearch from "@/components/MasterSearch";
import VenueCard, { VenueCardSkeleton } from "@/components/VenueCard";
import { Filter } from "lucide-react";

const CATEGORIES = [
  ["", "All"],
  ["banquet_hall", "Banquet Halls"],
  ["lawn", "Lawns"],
  ["catering", "Catering"],
  ["hotel_banquet", "Hotel Banquets"],
  ["decoration", "Decoration"],
];

export default function Properties() {
  const [sp] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");
  const [premium, setPremium] = useState([]);

  const city = sp.get("city");
  const q = sp.get("q");
  const pax_min = sp.get("pax_min");
  const veg = sp.get("veg");

  useEffect(() => {
    setLoading(true);
    const params = { city, q, pax_min, veg, category: cat || undefined };
    api.get("/venues", { params }).then((r)=>{
      setVenues(r.data);
      setPremium(r.data.filter(v=>v.highlighted).slice(0,10));
    }).finally(()=>setLoading(false));
  }, [city, q, pax_min, veg, cat]);

  return (
    <div>
      <section className="bg-gradient-to-b from-[color:var(--ivory-2)] to-transparent border-b border-[color:var(--line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
          <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Discover</div>
          <h1 className="font-display text-4xl md:text-5xl mt-1">
            Venues {city ? <>in <span className="text-[color:var(--wine)]">{city}</span></> : "across India"}
          </h1>
          <p className="text-neutral-600 mt-2 text-sm">{venues.length} listings match your criteria</p>
          <div className="mt-6"><MasterSearch defaultCity={city || ""}/></div>
        </div>
      </section>

      {premium.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-8" data-testid="listing-premium">
          <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)] mb-3">Premium Spotlight</div>
          <div className="overflow-hidden">
            <div className="marquee">
              {[...premium, ...premium].map((v, i)=>(
                <Link to={`/property/${v.id}`} key={i} className="min-w-[220px] max-w-[220px] card-luxury overflow-hidden block">
                  <img src={v.images?.[0]} className="h-32 w-full object-cover" alt={v.name}/>
                  <div className="p-3">
                    <div className="font-display text-base leading-tight">{v.name}</div>
                    <div className="text-[10px] text-neutral-500 uppercase mt-1">{v.city}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex gap-8">
        <aside className="hidden lg:block w-56 shrink-0 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4"><Filter className="w-4 h-4"/> <span className="text-sm font-medium">Category</span></div>
            <div className="flex flex-col gap-2">
              {CATEGORIES.map(([k, l])=>(
                <button key={k} onClick={()=>setCat(k)} className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${cat===k ? "bg-[color:var(--wine)] text-[color:var(--gold-2)]" : "hover:bg-[color:var(--ivory-2)]"}`} data-testid={`filter-cat-${k||"all"}`}>{l}</button>
              ))}
            </div>
          </div>
        </aside>
        <div className="flex-1">
          <div className="lg:hidden mb-4">
            <select value={cat} onChange={(e)=>setCat(e.target.value)} className="px-3 py-2 rounded-lg border w-full">
              {CATEGORIES.map(([k, l])=><option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {loading && Array.from({length:6}).map((_,i)=><VenueCardSkeleton key={i}/>)}
            {!loading && venues.length === 0 && (
              <div className="col-span-3 text-center py-24 text-neutral-500 border border-dashed rounded-2xl">
                No venues match. Try broadening your search.
              </div>
            )}
            {!loading && venues.map((v)=><VenueCard key={v.id} v={v}/>)}
          </div>
        </div>
      </section>
    </div>
  );
}
